import { DataLayer } from '@piwikpro/react-piwik-pro';
import React, { useCallback, useEffect } from 'react';

import { Link as GatsbyLink, graphql, navigate, PageProps } from 'gatsby';

import { styled, Theme, useTheme } from '@mui/material';

import { isCleanroom, isClothing, ServiceSegmentType } from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  RelatedProducts,
  setARIATranslations,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../libs/alternative-links';
import t from '../../../libs/translate';
import { useConversionButtonLabel } from '../../a-b-testing/cta-label-experiment';
import Image from '../../components/image';
import { ProductFallbackImage } from '../../components/product-fallback-image';
import { isMewaCircle } from '../../components/strapi/mewa-essentials/mewa-circle';
import { StrapiMewaEssentials } from '../../components/strapi/mewa-essentials/mewa-essentials';
import { jpgPath } from '../../helpers/image-factory';
import { useWindowHashNavigation } from '../../hooks/use-window-hash-navigation';
import { prepareBrochureSelection } from '../conversion/brochures-download/helper';
import { storeBrochures, storeServiceSegment } from '../conversion/persistence';
import DataSectionClothing from './data-section-clothing';
import DataSectionNonClothing from './data-section-non-clothing';
import { PageContext, Props } from './types';
import VariantsSlider from './variants-slider/variants-slider';

export { Head } from '../../layouts/head';

export const query = graphql`
  query (
    $marketRegex: String!
    $id: String!
    $lang: String!
    $locale: String!
    $serviceSegmentUuid: String
  ) {
    ...NavigationFragment

    productDetailPage: strapiProductDetailPage(locale: { eq: $locale }) {
      mewaEssentialsClothing {
        ...MewaEssentialsFragment
      }
      mewaEssentialsNonClothing {
        ...MewaEssentialsFragment
      }
    }

    strapiServiceSegment(
      locale: { eq: $locale }
      pimUuid: { eq: $serviceSegmentUuid }
    ) {
      mewaCircle {
        ...MewaCircleFragment
      }
    }

    pimProduct(id: { eq: $id }) {
      id
      displayName
      modelName
      h1
      infoText
      keyFacts

      serviceSegment {
        key
        category
        slug
        name
        relatedProducts: productGroups {
          products {
            modelName
            slug
            keyFacts
            variants {
              assetId
            }
            id
          }
        }
      }

      collection {
        name
        slug
      }

      variants {
        name
        slug
        hash
        colorName
        assetId
        emotions
        sizes
        material
        weightInG
        colors
        components {
          title
          items {
            text
            assetId
          }
        }
      }
    }

    industries: allPimIndustry(
      filter: {
        serviceSegments: {
          elemMatch: {
            productGroups: {
              elemMatch: { products: { elemMatch: { id: { eq: $id } } } }
            }
          }
        }
      }
    ) {
      nodes {
        name
      }
    }

    translations: translation(locale: { eq: $locale }) {
      general_download_brochure
      service_test_page_subtitle
      service_test_page_book_now
      product_details_consultation_teaser_description
      product_details_servicetest_teaser_description
      product_details_overview
      product_details_keyfacts
      product_details_weight
      product_details_purpose
      product_details_sizes
      product_details_sizes_note
      product_details_features
      product_details_material
      product_details_fabric
      product_details_logo_teaser_headline
      product_details_logo_teaser_description
      product_details_related_products_headline
      product_details_variants_count
      product_details_go_to_collection
      show_product_details_label
      product_details_consultation_teaser_mbs_headline1
      product_details_consultation_teaser_mbs_headline2
      product_details_consultation_teaser_mbs_description

      general_form_button_label_next
      general_form_button_label_back
    }
  }
`;

const categoriesForRelatedProducts = [
  ServiceSegmentType.carCovers,
  ServiceSegmentType.cleaningCloths,
];

const ProductDetails = ({
  data,
  pageContext,
}: PageProps<Props, PageContext>) => {
  const { activeItem: activeVariantIndex, handleNavigationChange } =
    useWindowHashNavigation(data.pimProduct.variants, false);

  const theme = useTheme();

  const { ctaLabel } = useConversionButtonLabel({
    translations: data.headerAndFooterTranslations,
  });

  // The product detail pages generate a link with a hash.
  // This scrolling behaviour prevents searching for an anchor element on the site.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigateToBrochuresDownload = useCallback(() => {
    // Using the session storage instead of the location state makes the logic
    // with preselection & remembering a selection after a reload a bit
    // simpler.
    storeBrochures([
      prepareBrochureSelection(data.pimProduct.serviceSegment.key),
    ]);

    DataLayer.push({ event: 'cta_brochure_download_detailpage_teaser' });

    navigate(
      `${pageContext.langPrefix}/${data.navigationLinks.conversion.brochuresDownload.slug}`,
    );
  }, [data, pageContext]);

  const navigateToConversion = useCallback(
    (trackingEventName: string) => () => {
      // Using the session storage instead of the location state makes the logic
      // with preselection & remembering a selection after a reload a bit
      // simpler.
      storeServiceSegment(data.pimProduct.serviceSegment.key);

      DataLayer.push({
        event: trackingEventName,
      });

      navigate(
        `${pageContext.langPrefix}/${data.navigationLinks.conversion.contact.slug}`,
      );
    },
    [data, pageContext],
  );

  const isClothingServiceSegment = isClothing(data.pimProduct.serviceSegment);

  const contactLink = {
    onClick: navigateToConversion('cta_contact_detailpage_inline_teaser'),
    buttonLabel: ctaLabel,
    description: t(
      data.translations,
      'product_details_consultation_teaser_description',
    ),
  };

  const serviceTestLink = {
    onClick: () => {
      DataLayer.push({ event: 'cta_cloth_service_test_detailpage_teaser' });

      navigate(
        `${pageContext.langPrefix}/${data.navigationLinks.conversion.cleaningClothsServiceTestPage.slug}`,
      );
    },
    buttonLabel: t(data.translations, 'service_test_page_book_now'),
    description: t(
      data.translations,
      'product_details_servicetest_teaser_description',
    ),
  };

  const activeVariant = data.pimProduct.variants[activeVariantIndex];

  const relatedProducts = React.useMemo(
    () =>
      data.pimProduct.serviceSegment.relatedProducts
        .flatMap((productGroup) => productGroup.products)
        .filter((product) => product.id !== data.pimProduct.id)
        .map((product) => ({
          ...product,
          image: product.variants[0]?.assetId ? (
            <Image
              alt={product.modelName}
              images={getImages(product.variants[0]?.assetId, theme)}
            />
          ) : (
            <ProductFallbackImage
              useMbsFallbackIcon={isClothingServiceSegment}
            />
          ),
        })),
    [data, theme, isClothingServiceSegment],
  );

  const accordionData = {
    overview: {
      summary: t(data.translations, 'product_details_overview'),
      keyFacts: {
        title: t(data.translations, 'product_details_keyfacts'),
        items: data.pimProduct.keyFacts,
      },
      weightInG: {
        title: t(data.translations, 'product_details_weight'),
        value: activeVariant.weightInG,
      },
      sizes: isClothingServiceSegment
        ? {
            title: t(data.translations, 'product_details_sizes'),
            value: t(data.translations, 'product_details_sizes_note'),
          }
        : undefined,
      industries: {
        title: t(data.translations, 'product_details_purpose'),
        items: data.industries.nodes.map((e) => e.name),
      },
    },
    productFeatures: {
      summary: t(data.translations, 'product_details_features'),
      components: activeVariant.components,
    },
    material: {
      summary: t(data.translations, 'product_details_material'),
      rows: [
        {
          title: t(data.translations, 'product_details_fabric'),
          value: activeVariant.material,
        },
      ],
    },
  };

  const variantsCountText = t(
    data.translations,
    'product_details_variants_count',
    { variantsCount: data.pimProduct.variants.length.toString() },
  );

  const logoTeaser = {
    headline: t(data.translations, 'product_details_logo_teaser_headline'),
    description: t(
      data.translations,
      'product_details_logo_teaser_description',
    ),
  };

  const conversionTeaserSide = {
    headline1: t(
      data.translations,
      'product_details_consultation_teaser_mbs_headline1',
    ),
    headline2: t(
      data.translations,
      'product_details_consultation_teaser_mbs_headline2',
    ),
    description: t(
      data.translations,
      'product_details_consultation_teaser_mbs_description',
    ),
    onClick: navigateToConversion('cta_contact_detailpage_side_teaser'),
  };

  const productMenu = data.navigationLinks.navigation.productMenu;
  const product = data.pimProduct;

  const categoryName = isClothing(product.serviceSegment)
    ? data.navigationLinks.navigation.clothing.name
    : isCleanroom(product.serviceSegment)
      ? data.navigationLinks.navigation.cleanroom.name
      : data.navigationLinks.navigation.nonClothing.name;

  const categorySlug = isClothing(product.serviceSegment)
    ? data.navigationLinks.navigation.clothing.slug
    : isCleanroom(product.serviceSegment)
      ? data.navigationLinks.navigation.cleanroom.slug
      : data.navigationLinks.navigation.nonClothing.slug;

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: productMenu.name,
      slug: `${pageContext.langPrefix}/${productMenu.slug}/`,
    },
    {
      name: categoryName,
      slug: `${pageContext.langPrefix}/${categorySlug}/`,
    },
    {
      name: product.serviceSegment.name,
      slug: `${pageContext.langPrefix}/${product.serviceSegment.slug}/`,
    },
  ];

  if (product.collection !== null) {
    breadCrumbData.push({
      name: product.collection?.name,
      slug: `${pageContext.langPrefix}/${product.collection?.slug}/`,
    });
  }
  breadCrumbData.push({
    name: product.displayName,
    slug: '',
  });

  const mobileBreadCrumbData: BreadCrumbData = isClothingServiceSegment
    ? {
        name: product.collection?.name ?? product.serviceSegment.name,
        slug:
          product.collection !== null
            ? `${pageContext.langPrefix}/${product.collection?.slug}`
            : `${pageContext.langPrefix}/${product.serviceSegment.slug}`,
      }
    : {
        name: product.serviceSegment.name,
        slug: `${pageContext.langPrefix}/${product.serviceSegment.slug}`,
      };

  const mewaEssentials = isClothingServiceSegment
    ? data.productDetailPage.mewaEssentialsClothing
    : data.productDetailPage.mewaEssentialsNonClothing;

  const isCloth = data.pimProduct.serviceSegment.key === 'cleaningCloths';

  if (
    !isClothingServiceSegment &&
    !isCloth &&
    mewaEssentials &&
    mewaEssentials.sections
  ) {
    mewaEssentials.sections = mewaEssentials.sections.filter(
      (section) => section.strapi_component !== 'mewa-essentials.costs',
    );
  }

  const mewaEssentialsCircleSection = mewaEssentials?.sections?.find(
    (section) => isMewaCircle(section),
  );

  if (mewaEssentialsCircleSection && data.strapiServiceSegment?.mewaCircle) {
    mewaEssentialsCircleSection.circle = data.strapiServiceSegment.mewaCircle;
  }

  setARIATranslations({
    label_back: data.translations.general_form_button_label_back,
    label_next: data.translations.general_form_button_label_next,
  });

  return (
    <Container
      data-pagefind-meta={`
      product_is_clothing:${Boolean(isClothingServiceSegment).toString()}`}
    >
      <div
        data-pagefind-meta={`service_segment_name:${product.serviceSegment.name}`}
        style={{ visibility: 'hidden' }}
      />
      <BreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(pageContext.locale)}
        LinkComponent={GatsbyLink}
      />
      {isClothingServiceSegment ? (
        <>
          <VariantsSlider
            data={data}
            activeIndex={activeVariantIndex}
            onSelectionChange={handleNavigationChange}
            variantsCountText={variantsCountText}
          />
          <DataSectionClothing
            data={data}
            accordionData={accordionData}
            contactLink={contactLink}
            onBrochuresDownload={navigateToBrochuresDownload}
            logoTeaser={logoTeaser}
            conversionTeaserSide={conversionTeaserSide}
          />
        </>
      ) : (
        <DataSectionNonClothing
          data={data}
          accordionData={accordionData}
          activeVariantIndex={activeVariantIndex}
          serviceTestLink={serviceTestLink}
          contactLink={contactLink}
          onBrochuresDownload={navigateToBrochuresDownload}
          onSelectionChange={handleNavigationChange}
          variantsCountText={variantsCountText}
        />
      )}
      {categoriesForRelatedProducts.includes(
        data.pimProduct.serviceSegment.key,
      ) && (
        <RelatedProducts
          data-pagefind-ignore
          headline={t(
            data.translations,
            'product_details_related_products_headline',
          )}
          products={relatedProducts}
          Link={GatsbyLink}
          langPrefix={pageContext.langPrefix}
          ctaLabel={t(data.translations, 'show_product_details_label')}
        />
      )}
      <StrapiMewaEssentials
        mewaEssentials={mewaEssentials}
        lang={pageContext.lang}
        pressOverviewSlug={`${pageContext.langPrefix}/${data.navigationLinks.pressOverview.slug}`}
      />
    </Container>
  );
};

export default ProductDetails;

const Container = styled('div')({});

const getImages = (assetId: string, theme: Theme) => [
  {
    src: jpgPath(assetId, 300, 300),
    media: `(max-width: ${theme.breakpoints.values.sm - 1}px)`,
    width: 300,
    height: 300,
  },
  {
    src: jpgPath(assetId, 500, 500),
    media: `(min-width: ${theme.breakpoints.values.sm}px)`,
    width: 500,
    height: 500,
  },
];
